import {
  ApolloClient,
  CombinedGraphQLErrors,
  InMemoryCache,
  Observable,
  createHttpLink,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GQL_URI } from "@config/index";
import { getJWTToken } from "./auth.util";

let isRefreshing = false;
let requestQueue: Array<(token: string) => void> = [];

const refreshToken = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      requestQueue.push(resolve);
    });
  }

  isRefreshing = true;
  try {
    const token = await getJWTToken();
    if (!token) {
      throw new Error("No token");
    }
    requestQueue.forEach((cb) => cb(token));
    requestQueue = [];
    return token;
  } catch (error) {
    requestQueue.forEach((cb) => cb(""));
    requestQueue = [];
    // eslint-disable-next-line no-console
    console.error("Token refresh failed:", error);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

const httpLink = createHttpLink({
  uri: GQL_URI,
});

const authLink = setContext(async (_, { headers }) => {
  try {
    const token = await getJWTToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : undefined,
      },
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error retrieving access token:", error);
    return { headers };
  }
});

const errorLink = onError(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    for (const err of error.errors) {
      if (err?.message === "Unauthorized") {
        return new Observable((observer) => {
          refreshToken()
            .then((newToken) => {
              if (!newToken) {
                observer.error(new Error("Unable to refresh token."));
                return;
              }

              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  Authorization: `Bearer ${newToken}`,
                },
              }));

              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch((error) => {
              observer.error(error);
            });
        });
      }
    }
  }
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      User: {
        keyFields: ["sub"],
      },
      Page: {
        keyFields: ["id"],
      },
      Question: {
        keyFields: ["id"],
      },
    },
  }),
});
